// TanStack Query hooks for the Contracts, Billing Rates, Invoices and
// Recurring-Invoices module. Mirrors the four backend routers:
//   packages/api/src/routes/contracts.ts          (/api/contracts)
//   packages/api/src/routes/billing-rates.ts       (/api/billing-rates)
//   packages/api/src/routes/invoices.ts            (/api/invoices, nested line-items)
//   packages/api/src/routes/recurring-invoices.ts  (/api/recurring-invoices)
//
// Money + quantity columns stay strings end-to-end (NUMERIC, never JS floats).
// Computed columns (invoice.total, line_item.line_total) are server-generated
// and never sent by the client. Timestamps are epoch-ms numbers.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  // Billing rates
  BillingRateResponse,
  BillingRatesListResponse,
  BillingServiceType,
  CreateBillingRateInput,
  UpdateBillingRateInput,
  // Contracts
  ContractResponse,
  ContractsListResponse,
  ContractStatus,
  CreateContractInput,
  UpdateContractInput,
  // Invoices
  CreateInvoiceInput,
  CreateInvoiceLineItemInput,
  InvoiceLineItemResponse,
  InvoiceResponse,
  InvoiceStatus,
  InvoicesListResponse,
  UpdateInvoiceInput,
  UpdateInvoiceLineItemInput,
  // Recurring invoices
  CreateRecurringInvoiceInput,
  RecurringInvoiceResponse,
  RecurringInvoicesListResponse,
  UpdateRecurringInvoiceInput,
} from '@dsr/shared-types';
import { api } from '../client';

// =============================================================================
// Contracts
// =============================================================================

const CONTRACTS_KEY = 'contracts';

export interface ContractFilters {
  organization_id?: number;
  status?: ContractStatus;
  /** Surface contracts expiring on or before this epoch-ms instant. */
  expiring_before?: number;
}

function buildContractQuery(filters: ContractFilters): string {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.expiring_before !== undefined) {
    params.set('expiring_before', String(filters.expiring_before));
  }
  return params.toString();
}

export function useContracts(filters: ContractFilters = {}) {
  const qs = buildContractQuery(filters);
  const url = qs.length > 0 ? `/api/contracts?${qs}` : '/api/contracts';
  return useQuery({
    queryKey: [CONTRACTS_KEY, 'list', { ...filters }],
    queryFn: ({ signal }) => api.get<ContractsListResponse>(url, signal),
  });
}

export function useContract(id: number | null) {
  return useQuery({
    queryKey: [CONTRACTS_KEY, 'detail', id ?? 0],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<ContractResponse>(`/api/contracts/${id}`, signal),
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractInput) =>
      api.post<ContractResponse>('/api/contracts', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [CONTRACTS_KEY] });
    },
  });
}

export interface UpdateContractArgs extends UpdateContractInput {
  id: number;
}

export function useUpdateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateContractArgs) =>
      api.patch<ContractResponse>(`/api/contracts/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [CONTRACTS_KEY] });
    },
  });
}

export function useDeleteContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/contracts/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [CONTRACTS_KEY] });
    },
  });
}

// =============================================================================
// Billing rates
// =============================================================================

const RATES_KEY = 'billing-rates';

export interface BillingRateFilters {
  organization_id?: number;
  /** Only the global default rate card (organization_id IS NULL). */
  global?: boolean;
  service_type?: BillingServiceType;
}

function buildRateQuery(filters: BillingRateFilters): string {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.global) params.set('global', 'true');
  if (filters.service_type) params.set('service_type', filters.service_type);
  return params.toString();
}

export function useBillingRates(filters: BillingRateFilters = {}) {
  const qs = buildRateQuery(filters);
  const url = qs.length > 0 ? `/api/billing-rates?${qs}` : '/api/billing-rates';
  return useQuery({
    queryKey: [RATES_KEY, 'list', { ...filters }],
    queryFn: ({ signal }) => api.get<BillingRatesListResponse>(url, signal),
  });
}

export function useBillingRate(id: number | null) {
  return useQuery({
    queryKey: [RATES_KEY, 'detail', id ?? 0],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<BillingRateResponse>(`/api/billing-rates/${id}`, signal),
  });
}

export function useCreateBillingRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBillingRateInput) =>
      api.post<BillingRateResponse>('/api/billing-rates', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [RATES_KEY] });
    },
  });
}

export interface UpdateBillingRateArgs extends UpdateBillingRateInput {
  id: number;
}

export function useUpdateBillingRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateBillingRateArgs) =>
      api.patch<BillingRateResponse>(`/api/billing-rates/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [RATES_KEY] });
    },
  });
}

export function useDeleteBillingRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/billing-rates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [RATES_KEY] });
    },
  });
}

// =============================================================================
// Invoices (+ nested line items)
// =============================================================================

const INVOICES_KEY = 'invoices';

export const invoiceKeys = {
  all: [INVOICES_KEY] as const,
  lists: () => [INVOICES_KEY, 'list'] as const,
  list: (filters: InvoiceFilters) => [INVOICES_KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [INVOICES_KEY, 'detail', id] as const,
};

export interface InvoiceFilters {
  organization_id?: number;
  contract_id?: number;
  status?: InvoiceStatus;
}

function buildInvoiceQuery(filters: InvoiceFilters): string {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.contract_id !== undefined) {
    params.set('contract_id', String(filters.contract_id));
  }
  if (filters.status) params.set('status', filters.status);
  return params.toString();
}

export function useInvoices(filters: InvoiceFilters = {}) {
  const qs = buildInvoiceQuery(filters);
  const url = qs.length > 0 ? `/api/invoices?${qs}` : '/api/invoices';
  return useQuery({
    queryKey: invoiceKeys.list(filters),
    queryFn: ({ signal }) => api.get<InvoicesListResponse>(url, signal),
  });
}

// Detail response carries the invoice WITH its line items.
export function useInvoice(id: number | null) {
  return useQuery({
    queryKey: invoiceKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<InvoiceResponse>(`/api/invoices/${id}`, signal),
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) =>
      api.post<InvoiceResponse>('/api/invoices', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

export interface UpdateInvoiceArgs extends UpdateInvoiceInput {
  id: number;
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateInvoiceArgs) =>
      api.patch<InvoiceResponse>(`/api/invoices/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

export function useSendInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<InvoiceResponse>(`/api/invoices/${id}/send`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<InvoiceResponse>(`/api/invoices/${id}/mark-paid`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<InvoiceResponse>(`/api/invoices/${id}/void`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/invoices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

// ---------- Invoice line items (nested under an invoice) ----------
//
// Mutating a line item shifts the invoice's computed totals only after the
// caller PATCHes subtotal/tax_amount, but the line_total is regenerated by the
// DB immediately — so we invalidate the parent invoice detail on every write.

export interface CreateLineItemArgs extends CreateInvoiceLineItemInput {
  invoiceId: number;
}

export function useCreateLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, ...body }: CreateLineItemArgs) =>
      api.post<InvoiceLineItemResponse>(
        `/api/invoices/${invoiceId}/line-items`,
        body,
      ),
    onSuccess: (_data, { invoiceId }) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    },
  });
}

export interface UpdateLineItemArgs extends UpdateInvoiceLineItemInput {
  invoiceId: number;
  lineId: number;
}

export function useUpdateLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId, ...body }: UpdateLineItemArgs) =>
      api.patch<InvoiceLineItemResponse>(
        `/api/invoices/${invoiceId}/line-items/${lineId}`,
        body,
      ),
    onSuccess: (_data, { invoiceId }) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    },
  });
}

export interface DeleteLineItemArgs {
  invoiceId: number;
  lineId: number;
}

export function useDeleteLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId }: DeleteLineItemArgs) =>
      api.delete<void>(`/api/invoices/${invoiceId}/line-items/${lineId}`),
    onSuccess: (_data, { invoiceId }) => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    },
  });
}

// =============================================================================
// Recurring invoices
// =============================================================================

const RECURRING_KEY = 'recurring-invoices';

export interface RecurringInvoiceFilters {
  organization_id?: number;
  contract_id?: number;
  active?: boolean;
}

function buildRecurringQuery(filters: RecurringInvoiceFilters): string {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.contract_id !== undefined) {
    params.set('contract_id', String(filters.contract_id));
  }
  if (filters.active !== undefined) params.set('active', String(filters.active));
  return params.toString();
}

export function useRecurringInvoices(filters: RecurringInvoiceFilters = {}) {
  const qs = buildRecurringQuery(filters);
  const url =
    qs.length > 0 ? `/api/recurring-invoices?${qs}` : '/api/recurring-invoices';
  return useQuery({
    queryKey: [RECURRING_KEY, 'list', { ...filters }],
    queryFn: ({ signal }) => api.get<RecurringInvoicesListResponse>(url, signal),
  });
}

export function useRecurringInvoice(id: number | null) {
  return useQuery({
    queryKey: [RECURRING_KEY, 'detail', id ?? 0],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<RecurringInvoiceResponse>(`/api/recurring-invoices/${id}`, signal),
  });
}

export function useCreateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecurringInvoiceInput) =>
      api.post<RecurringInvoiceResponse>('/api/recurring-invoices', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [RECURRING_KEY] });
    },
  });
}

export interface UpdateRecurringInvoiceArgs extends UpdateRecurringInvoiceInput {
  id: number;
}

export function useUpdateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateRecurringInvoiceArgs) =>
      api.patch<RecurringInvoiceResponse>(`/api/recurring-invoices/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [RECURRING_KEY] });
    },
  });
}

export function useDeleteRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<void>(`/api/recurring-invoices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [RECURRING_KEY] });
    },
  });
}
